import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { HelperService } from 'src/app/helpers/services/helper.service';
import { ImageDragNDropDirective } from './image-drag-n-drop.directive';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

type UploadState = 'initial' | 'loading' | 'preview' | 'error';

@Component({
    selector: 'app-image-upload',
    templateUrl: './image-upload.component.html',
    styleUrls: ['./image-upload.component.scss'],
    imports: [
    CommonModule,
    MatSnackBarModule,
    ImageDragNDropDirective,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule
]
})
export class ImageUploadComponent {
  uploadState: UploadState = 'initial';
  thumbnailPreview: SafeUrl;
  
  private file: File;
  
  @Input() value: string;
  @Output() uploadComplete = new EventEmitter<{sharedLink: string, thumbSharedLink: string}>();
  @Output() uploadError = new EventEmitter<void>();
  @Output() fileRemoved = new EventEmitter<void>();

  constructor(
    private _snackBar: MatSnackBar,
    private helperService: HelperService,
    private sanitizer: DomSanitizer
  ) {}

  onFileChange(pFileList: File[] | FileList) {
    const fileList = pFileList instanceof FileList ? Array.from(pFileList) : pFileList;
    if (fileList.length > 0) {
      this.file = fileList[0];
      this.uploadState = 'loading';
      
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.thumbnailPreview = this.sanitizer.bypassSecurityTrustUrl(e.target.result);
        this.onSave();
      };
      reader.onerror = () => {
        this.uploadState = 'error';
      };
      reader.readAsDataURL(this.file);
    }
  }

  onSave() {
    if (!this.file) return;

    let contentType = this.file.type;
    const extension = this.file.name.split('.').pop().toLowerCase();
    let fileName = `images-${new Date().getTime()}-${this.file.name.toLowerCase()}`;

    if (extension === 'ai') {
      fileName = `${new Date().getTime()}-${this.file.name.toLowerCase().replace('.ai', '.pdf')}`;
      contentType = 'application/pdf';
    } else if (['pdf'].includes(extension)) {
      fileName = `${new Date().getTime()}-${this.file.name.toLowerCase()}`;
    }

    // saveToDigitalOceanAndDB throws when the storage PUT is not ok, so a failed
    // upload never emits a URL to a missing object.
    const file = this.file;
    this.helperService.saveToDigitalOceanAndDB(fileName, contentType, file)
      .then(async () => {
        const sharedLink = this.helperService.buildPublicUrl(fileName);
        const thumbName = `thumbs-${fileName.replace('images-', '')}`;
        const thumbFile = await this.helperService.resizeImage(file, thumbName);
        await this.helperService.saveToDigitalOceanAndDB(thumbName, contentType, thumbFile);
        const thumbSharedLink = this.helperService.buildPublicUrl(thumbName);

        this.uploadState = 'preview';
        this.uploadComplete.emit({ sharedLink, thumbSharedLink });
      })
      .catch(() => this.handleUploadError());
  }

  private handleUploadError() {
    this.uploadState = 'error';
    this.thumbnailPreview = null;
    this._snackBar.open(`Error uploading file [where: ImageUploadComponent.handleUploadError]`, 'Close', { duration: 3000 });
    this.uploadError.emit();
  }

  removeFile(event: MouseEvent): void {
    event.stopPropagation();
    this.file = null;
    this.thumbnailPreview = null;
    this.uploadState = 'initial';
    this.fileRemoved.emit();
  }

  retryUpload(event: MouseEvent): void {
    event.stopPropagation();
    this.uploadState = 'initial';
  }
}
